import React, { useEffect, useState } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Trash2, Edit, Plus, X } from "lucide-react";

type DayKey = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

interface DayHours {
  open: boolean;
  start: string; // HH:mm
  end: string;   // HH:mm
}

interface TimeRange {
  start: string; // HH:mm
  end: string;   // HH:mm
}

interface DateOverride {
  id: string;
  override_date: string; // YYYY-MM-DD
  override_mode: 'closed' | 'custom_hours';
  notes?: string;
  time_ranges?: TimeRange[];
}

export default function HoursPage() {
  const [hours, setHours] = useState<Record<DayKey, DayHours>>({
    monday: { open: true, start: "09:00", end: "17:00" },
    tuesday: { open: true, start: "09:00", end: "17:00" },
    wednesday: { open: true, start: "09:00", end: "17:00" },
    thursday: { open: true, start: "09:00", end: "17:00" },
    friday: { open: true, start: "09:00", end: "17:00" },
    saturday: { open: false, start: "10:00", end: "14:00" },
    sunday: { open: false, start: "10:00", end: "14:00" },
  });

  const [dateOverrides, setDateOverrides] = useState<DateOverride[]>([]);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [editingOverride, setEditingOverride] = useState<DateOverride | null>(null);
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideMode, setOverrideMode] = useState<'closed' | 'custom_hours'>('closed');
  const [timeRanges, setTimeRanges] = useState<TimeRange[]>([{ start: "09:00", end: "17:00" }]);
  const [businessId, setBusinessId] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Business Hours | Figgy.link";
  }, []);

  const { user } = useAuth();

  // Load business hours
  useEffect(() => {
    const load = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const userId = authUser?.id || user?.id;
        if (!userId) return;
        const { data: business } = await supabase
          .from('business_profiles')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();
        if (!business) return;

        setBusinessId((business as any).id);

        const { data } = await supabase
          .from('business_hours')
          .select('*')
          .eq('business_id', (business as any).id)
          .eq('provider_id', null); // v1: default provider only
        if (data && data.length > 0) {
          const next: Record<DayKey, DayHours> = { ...hours };
          for (const row of data as any[]) {
            const key = row.day_of_week as DayKey;
            if (key in next) {
              next[key] = {
                open: Boolean(row.enabled),
                start: row.start_time || "09:00",
                end: row.end_time || "17:00",
              };
            }
          }
          setHours(next);
        }
      } catch (e) {
        console.error(e);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load date overrides
  useEffect(() => {
    const loadOverrides = async () => {
      if (!businessId) return;

      try {
        const { data: overrides, error } = await supabase
          .from('business_date_overrides')
          .select(`
            id,
            override_date,
            override_mode,
            notes,
            business_date_time_ranges(start_time, end_time, sort_order)
          `)
          .eq('business_id', businessId)
          .is('provider_id', null) // v1: only default provider
          .order('override_date', { ascending: true });

        if (error) throw error;

        const formatted = (overrides || []).map((o: any) => ({
          id: o.id,
          override_date: o.override_date,
          override_mode: o.override_mode,
          notes: o.notes,
          time_ranges: o.business_date_time_ranges
            ?.sort((a: any, b: any) => a.sort_order - b.sort_order)
            .map((r: any) => ({ start: r.start_time, end: r.end_time })) || []
        }));

        setDateOverrides(formatted);
      } catch (e) {
        console.error('Error loading date overrides:', e);
      }
    };

    loadOverrides();
  }, [businessId]);

  const dayLabels: Record<DayKey, string> = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  const updateDay = (day: DayKey, patch: Partial<DayHours>) => {
    setHours((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  };

  const saveHours = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userId = authUser?.id || user?.id;
      if (!userId) {
        toast.error("Please sign in to save hours");
        return;
      }
      const { data: business } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      if (!business) {
        toast.error("Business profile not found");
        return;
      }

      const rows = Object.entries(hours).map(([day, v]) => ({
        business_id: (business as any).id,
        day_of_week: day,
        start_time: v.start,
        end_time: v.end,
        enabled: v.open,
        provider_id: null // v1: default provider
      }));

      const { error } = await supabase
        .from('business_hours')
        .upsert(rows, { onConflict: 'business_id,provider_id,day_of_week' });
      if (error) throw error;
      toast.success("Hours saved successfully!");
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Failed to save hours');
    }
  };

  const openAddOverrideDialog = () => {
    setEditingOverride(null);
    setOverrideDate("");
    setOverrideMode('closed');
    setTimeRanges([{ start: "09:00", end: "17:00" }]);
    setShowOverrideDialog(true);
  };

  const openEditOverrideDialog = (override: DateOverride) => {
    setEditingOverride(override);
    setOverrideDate(override.override_date);
    setOverrideMode(override.override_mode);
    setTimeRanges(override.time_ranges && override.time_ranges.length > 0
      ? override.time_ranges
      : [{ start: "09:00", end: "17:00" }]
    );
    setShowOverrideDialog(true);
  };

  const addTimeRange = () => {
    setTimeRanges([...timeRanges, { start: "09:00", end: "17:00" }]);
  };

  const removeTimeRange = (index: number) => {
    setTimeRanges(timeRanges.filter((_, i) => i !== index));
  };

  const updateTimeRange = (index: number, field: 'start' | 'end', value: string) => {
    const updated = [...timeRanges];
    updated[index][field] = value;
    setTimeRanges(updated);
  };

  const saveOverride = async () => {
    if (!businessId) {
      toast.error("Business not found");
      return;
    }

    if (!overrideDate) {
      toast.error("Date is required");
      return;
    }

    // Validate time ranges if custom_hours mode
    if (overrideMode === 'custom_hours') {
      if (timeRanges.length === 0) {
        toast.error("At least one time range is required for custom hours");
        return;
      }

      for (const range of timeRanges) {
        if (range.start >= range.end) {
          toast.error("Start time must be before end time");
          return;
        }
      }
    }

    try {
      // Upsert date override
      const { data: override, error: overrideError } = await supabase
        .from('business_date_overrides')
        .upsert({
          id: editingOverride?.id,
          business_id: businessId,
          provider_id: null, // v1: default provider
          override_date: overrideDate,
          override_mode: overrideMode
        }, {
          onConflict: 'business_id,provider_id,override_date'
        })
        .select('id')
        .single();

      if (overrideError) throw overrideError;

      // If custom_hours, upsert time ranges
      if (overrideMode === 'custom_hours' && override) {
        // Delete existing ranges
        await supabase
          .from('business_date_time_ranges')
          .delete()
          .eq('override_id', override.id);

        // Insert new ranges
        const rangesData = timeRanges.map((range, index) => ({
          override_id: override.id,
          start_time: range.start,
          end_time: range.end,
          sort_order: index
        }));

        const { error: rangesError } = await supabase
          .from('business_date_time_ranges')
          .insert(rangesData);

        if (rangesError) throw rangesError;
      } else if (overrideMode === 'closed' && override) {
        // Delete time ranges for closed overrides
        await supabase
          .from('business_date_time_ranges')
          .delete()
          .eq('override_id', override.id);
      }

      toast.success(editingOverride ? "Override updated" : "Override created");
      setShowOverrideDialog(false);

      // Reload overrides
      const { data: overrides } = await supabase
        .from('business_date_overrides')
        .select(`
          id,
          override_date,
          override_mode,
          notes,
          business_date_time_ranges(start_time, end_time, sort_order)
        `)
        .eq('business_id', businessId)
        .is('provider_id', null)
        .order('override_date', { ascending: true });

      const formatted = (overrides || []).map((o: any) => ({
        id: o.id,
        override_date: o.override_date,
        override_mode: o.override_mode,
        notes: o.notes,
        time_ranges: o.business_date_time_ranges
          ?.sort((a: any, b: any) => a.sort_order - b.sort_order)
          .map((r: any) => ({ start: r.start_time, end: r.end_time })) || []
      }));

      setDateOverrides(formatted);
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Failed to save override');
    }
  };

  const deleteOverride = async (overrideId: string) => {
    if (!confirm("Are you sure you want to delete this override?")) return;

    try {
      const { error } = await supabase
        .from('business_date_overrides')
        .delete()
        .eq('id', overrideId);

      if (error) throw error;

      toast.success("Override deleted");
      setDateOverrides(dateOverrides.filter(o => o.id !== overrideId));
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Failed to delete override');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTodayString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  /**
   * Format HH:mm time to 12-hour format with AM/PM
   * e.g., "09:00" → "9:00 AM", "14:30" → "2:30 PM"
   * Handles malformed inputs gracefully (returns original string if invalid)
   */
  const formatTime = (time: string): string => {
    if (!time || typeof time !== 'string') {
      return time || '';
    }

    const parts = time.split(':');
    if (parts.length < 2) {
      // Malformed time string (missing minutes) - return as-is
      console.warn(`Invalid time format: ${time}`);
      return time;
    }

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (isNaN(hours) || isNaN(minutes)) {
      // Non-numeric values - return as-is
      console.warn(`Invalid time values: ${time}`);
      return time;
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  return (
    <div className="min-h-screen content-wrapper">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <h1 className="text-2xl font-semibold text-white on-dark-bg">Business Hours</h1>

          {/* Weekly Schedule */}
          <Card className="bg-black/40 backdrop-blur-xl border border-white/10">
            <CardHeader>
              <CardTitle>Weekly Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.keys(dayLabels).map((key) => {
                const day = key as DayKey;
                const dh = hours[day];
                return (
                  <div key={day} className="grid grid-cols-12 items-center gap-3 border border-white/10 rounded-md p-3">
                    <div className="col-span-12 md:col-span-3 font-medium text-white">{dayLabels[day]}</div>
                    <div className="col-span-12 md:col-span-2 flex items-center gap-2">
                      <Switch checked={dh.open} onCheckedChange={(v) => updateDay(day, { open: v })} />
                      <span className="text-sm text-gray-300">{dh.open ? "Open" : "Closed"}</span>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <Label htmlFor={`${day}-start`} className="text-xs">Start</Label>
                      <Input id={`${day}-start`} type="time" value={dh.start} onChange={(e) => updateDay(day, { start: e.target.value })} />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <Label htmlFor={`${day}-end`} className="text-xs">End</Label>
                      <Input id={`${day}-end`} type="time" value={dh.end} onChange={(e) => updateDay(day, { end: e.target.value })} />
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-end">
                <Button onClick={saveHours}>Save</Button>
              </div>
            </CardContent>
          </Card>

          {/* Date Overrides */}
          <Card className="bg-black/40 backdrop-blur-xl border border-white/10">
            <CardHeader>
              <CardTitle>Date Overrides</CardTitle>
              <CardDescription>
                Set custom hours or closures for specific dates (holidays, events, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {dateOverrides.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dateOverrides.map((override) => (
                      <TableRow key={override.id}>
                        <TableCell className="font-medium">
                          {formatDate(override.override_date)}
                        </TableCell>
                        <TableCell>
                          {override.override_mode === 'closed' ? (
                            <Badge variant="destructive">Closed</Badge>
                          ) : (
                            <Badge variant="secondary">Custom Hours</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {override.override_mode === 'custom_hours' && override.time_ranges && (
                            <div className="text-sm text-gray-300 space-y-1">
                              {override.time_ranges.map((range, idx) => (
                                <div key={idx}>{formatTime(range.start)} - {formatTime(range.end)}</div>
                              ))}
                            </div>
                          )}
                          {override.override_mode === 'closed' && (
                            <span className="text-sm text-gray-400">All day</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditOverrideDialog(override)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteOverride(override.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  No date overrides configured. Add one to override your weekly schedule for specific dates.
                </p>
              )}

              <Button onClick={openAddOverrideDialog} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Date Override
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOverride ? 'Edit' : 'Add'} Date Override</DialogTitle>
            <DialogDescription>
              Configure a specific date to be closed or have custom hours
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Date picker */}
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                min={getTodayString()}
                value={overrideDate}
                onChange={(e) => setOverrideDate(e.target.value)}
              />
            </div>

            {/* Mode selector */}
            <div>
              <Label>Override Mode</Label>
              <RadioGroup value={overrideMode} onValueChange={(v) => setOverrideMode(v as 'closed' | 'custom_hours')}>
                <div className="flex items-center space-x-2 mt-2">
                  <RadioGroupItem value="closed" id="closed" />
                  <Label htmlFor="closed" className="font-normal">
                    Closed (no availability)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom_hours" id="custom_hours" />
                  <Label htmlFor="custom_hours" className="font-normal">
                    Custom Hours
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Time ranges (if custom_hours) */}
            {overrideMode === 'custom_hours' && (
              <div className="space-y-2">
                <Label>Time Ranges</Label>
                {timeRanges.map((range, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      type="time"
                      value={range.start}
                      onChange={(e) => updateTimeRange(idx, 'start', e.target.value)}
                      className="flex-1"
                    />
                    <span className="text-sm">to</span>
                    <Input
                      type="time"
                      value={range.end}
                      onChange={(e) => updateTimeRange(idx, 'end', e.target.value)}
                      className="flex-1"
                    />
                    {timeRanges.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeTimeRange(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addTimeRange}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Time Range
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveOverride}>
              {editingOverride ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
